import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { fmtInr, todayISO } from "../lib/format";

export default function CapitalSection({ clientId, initialCapital, startDate, onChange }) {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/advisor/clients/${clientId}/capital`);
      setItems(data);
    } catch {
      /* silent */
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!amount || Number(amount) <= 0 || !date) {
      toast.error("Enter date and positive amount");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post(`/advisor/clients/${clientId}/capital`, {
        date,
        amount: Number(amount),
        notes: notes || null,
      });
      toast.success("Capital addition recorded");
      setAmount("");
      setNotes("");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this capital entry?")) return;
    try {
      await apiClient.delete(`/advisor/clients/${clientId}/capital/${id}`);
      toast.success("Removed");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const totalAdditions = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalCapital = Number(initialCapital || 0) + totalAdditions;

  return (
    <section className="mb-8">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xl font-serif font-medium tracking-tight flex items-center gap-2">
          <Wallet className="h-4 w-4" strokeWidth={1.5} />
          Capital
        </h2>
        <div className="text-xs text-muted-foreground">
          Initial: <span className="font-mono">{fmtInr(initialCapital)}</span> ·
          Additions: <span className="font-mono">{fmtInr(totalAdditions)}</span> ·
          Total: <span className="font-mono text-foreground">{fmtInr(totalCapital)}</span>
        </div>
      </div>

      {/* Add form */}
      <div className="border border-subtle bg-card p-4 mb-3" data-testid="capital-add-form">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 sm:col-span-3">
            <label className="text-[11px] uppercase-track text-muted-foreground font-medium">
              Date
            </label>
            <input
              data-testid="capital-date"
              type="date"
              value={date}
              min={startDate}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring"
            />
          </div>
          <div className="col-span-12 sm:col-span-3">
            <label className="text-[11px] uppercase-track text-muted-foreground font-medium">
              Amount (₹)
            </label>
            <input
              data-testid="capital-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="500000"
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring tabular"
            />
          </div>
          <div className="col-span-12 sm:col-span-4">
            <label className="text-[11px] uppercase-track text-muted-foreground font-medium">
              Notes
            </label>
            <input
              data-testid="capital-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Top-up via NEFT"
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring"
            />
          </div>
          <div className="col-span-12 sm:col-span-2 flex items-end">
            <button
              data-testid="capital-add-btn"
              onClick={add}
              disabled={loading}
              className="w-full bg-lime hover:brightness-110 text-white px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98]"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              Add
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-subtle bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="capital-table">
          <thead className="bg-secondary border-b border-subtle">
            <tr className="text-[10px] uppercase-track text-muted-foreground">
              <th className="text-left p-3 font-medium">Date</th>
              <th className="text-left p-3 font-medium">Kind</th>
              <th className="text-right p-3 font-medium">Amount</th>
              <th className="text-left p-3 font-medium">Notes</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-subtle bg-secondary/40">
              <td className="p-3 font-mono tabular text-xs">{startDate}</td>
              <td className="p-3">
                <span className="text-[10px] px-2 py-0.5 font-semibold uppercase-track bg-gold-soft">
                  Initial
                </span>
              </td>
              <td className="p-3 text-right tabular font-mono">{fmtInr(initialCapital)}</td>
              <td className="p-3 text-muted-foreground italic">Portfolio inception</td>
              <td />
            </tr>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-subtle hover:bg-secondary">
                <td className="p-3 font-mono tabular text-xs">{it.date}</td>
                <td className="p-3">
                  <span className="text-[10px] px-2 py-0.5 font-semibold uppercase-track bg-profit-soft">
                    Addition
                  </span>
                </td>
                <td className="p-3 text-right tabular font-mono">{fmtInr(it.amount)}</td>
                <td className="p-3 text-muted-foreground">{it.notes || "—"}</td>
                <td className="p-3 text-right">
                  <button
                    data-testid={`capital-delete-${it.id}`}
                    onClick={() => remove(it.id)}
                    className="text-muted-foreground/70 hover:text-loss"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                  No additional capital contributions recorded.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
